import { type InputCapture } from '@canvas/input';
import type {
  PointerDownCapture,
  PointerMoveCapture,
  PointerUpCapture,
} from '@canvas/input/types/pointer';

import { type SelectionsMachine } from '../../../machine/selection-machine';
import { isCellSelected } from '../../../machine/utils/is-cell-selected';
import { isSameCell } from '../../../machine/utils/is-same-cell';
import { type GridPosition } from '../../../selections';
import { type AutoScroller, createAutoScroller } from '../utils/create-auto-scroller';
import { type ClickTarget } from '../utils/get-click-target';

import { dispatchInitialClick } from './dispatch-initial-click';

export interface CreateMouseStateMachineParams {
  machine: SelectionsMachine;
  inputCapture: InputCapture;
  getClickTarget: (x: number, y: number) => ClickTarget;
  getScrollContainer: () => HTMLElement | null;
  getMaxVisibleRowIndex: () => number;
  getVisibleEntryCount: () => number;
}

export interface MouseState {
  onPointerDown(event: PointerDownCapture): MouseState;
  onPointerMove(event: PointerMoveCapture): MouseState;
  onPointerUp(event: PointerUpCapture): MouseState;
}

interface MachineContext {
  /**
   * The grid position of the cell where the initial pointerdown event occurred.
   * Used to determine if the pointer was released on the same cell.
   */
  pointerDownCell: GridPosition | null;

  /**
   * Indicates whether the cell was already part of the active selection
   * before the current pointer interaction began.
   */
  wasAlreadySelected: boolean;

  /**
   * Flags whether the pointer has moved to a different cell during a drag interaction.
   * Prevents accidental static clicks when the user intended to drag.
   */
  hasDragged: boolean;

  /**
   * Tracks the most recent valid cell position during a fill drag operation.
   * This allows the fill to complete even when the pointer is released outside
   * the grid bounds (e.g., when the user drags beyond the table edge). Reset to
   * `null` when a new fill operation starts and after the fill pointer interaction ends.
   */
  lastValidFillCell: GridPosition | null;

  /**
   * Used to force auto scrolling when at the edges of the inner container.
   */
  autoScroller: AutoScroller | null;

  /**
   * Resets all context values to their initial idle state and safely cleans up
   * any active auto-scroller.
   */
  reset: VoidFunction;
}

function createMachineContext(): MachineContext {
  return {
    pointerDownCell: null,
    wasAlreadySelected: false,
    hasDragged: false,
    lastValidFillCell: null,
    autoScroller: null,

    reset() {
      // Clean up the active scroller before nullifying
      this.autoScroller?.stop();
      this.autoScroller = null;

      this.pointerDownCell = null;
      this.wasAlreadySelected = false;
      this.hasDragged = false;
      this.lastValidFillCell = null;
    },
  };
}

export function createMouseStateMachine(
  params: CreateMouseStateMachineParams
): MouseState {
  const {
    machine,
    inputCapture,
    getClickTarget,
    getScrollContainer,
    getMaxVisibleRowIndex,
    getVisibleEntryCount,
  } = params;
  const context = createMachineContext();

  function extendDragToCell(x: number, y: number) {
    const target = getClickTarget(x, y);

    if (target.type === 'cell') {
      machine.dispatch({
        type: 'extendToCell',
        cell: target.position,
        source: 'mouse',
        isDragging: true,
      });
    }
  }

  function extendFillToCell(x: number, y: number) {
    const target = getClickTarget(x, y);

    if (target.type === 'cell') {
      context.lastValidFillCell = target.position;

      machine.dispatch({
        type: 'fillDrag',
        cell: target.position,
        source: 'mouse',
      });
    }
  }

  const idleState: MouseState = {
    // There's no way to get a pointerup or pointermove
    // event in the idle state, so we just fill these with idleState
    onPointerUp: () => {
      context.reset();
      return idleState;
    },
    onPointerMove: () => idleState,
    onPointerDown: (event) => {
      const { x, y, button } = event.properties;

      // if the button is secondary, that means right-click, which is also context-menu
      // so we ignore it here.
      // this avoids a right click conflicting with a context menu
      if (button === 'secondary') {
        return idleState;
      }

      const target = getClickTarget(x, y);

      if (target.type === 'none') {
        return idleState;
      }

      const state = machine.getState();

      if (target.type === 'cell') {
        context.pointerDownCell = target.position;
        context.hasDragged = false;
        context.wasAlreadySelected = isCellSelected(state, target.position);
      } else {
        context.pointerDownCell = null;
        context.wasAlreadySelected = false;
      }

      /**
       * If we're editing currently, we may have to ignore, intercept, OR fire a stopEditing dispatch.
       */
      if (state.isEditing) {
        if (
          target.type === 'cell' &&
          state.focusedCell &&
          isSameCell(state.focusedCell, target.position)
        ) {
          // User clicked INSIDE the active editor (e.g., to move the text cursor).
          // We swallow the event completely. Do not dispatch selection events,
          // and strictly return idleState so we don't start dragging.
          return idleState;
        }

        // User clicked ELSEWHERE (a different cell, row header, etc.)
        // We gracefully commit the active edit before we let the selection change.
        machine.dispatch({ type: 'stopEditing', source: 'mouse' });
      }

      window.getSelection()?.removeAllRanges();

      if (target.type === 'outside') {
        machine.dispatch({ type: 'deselectAll', source: 'mouse' });

        // in case we were editing, we want to make sure the state is reset
        machine.dispatch({ type: 'stopEditing', source: 'mouse' });

        return idleState;
      }

      if (target.type === 'fillHandle') {
        context.lastValidFillCell = null;

        machine.dispatch({ type: 'fillStart', source: 'mouse' });

        return fillingState;
      }

      const hasShift = inputCapture.hasKeyModifierPressed('Shift');
      const hasMetaOrCtrl =
        inputCapture.hasKeyModifierPressed('Meta') ||
        inputCapture.hasKeyModifierPressed('Control');

      dispatchInitialClick(machine, target, {
        extend: hasShift,
        toggle: hasMetaOrCtrl,
        getVisibleEntryCount,
        getMaxVisibleRowIndex,
      });

      // if we're in a pointerdown state and we're not shift
      // clicking, we can transition to a dragging state
      if (target.type === 'cell' && !hasShift) {
        return draggingState;
      }

      return idleState;
    },
  };

  const draggingState: MouseState = {
    // If the pointer is down and we continue to get
    // pointerdown events, we ignore them
    onPointerDown: () => draggingState,

    onPointerMove: (event) => {
      const { x, y } = event.properties;
      const target = getClickTarget(x, y);

      if (
        target.type === 'cell' &&
        context.pointerDownCell &&
        !isSameCell(context.pointerDownCell, target.position)
      ) {
        context.hasDragged = true;
      }

      extendDragToCell(x, y);

      if (!context.autoScroller) {
        context.autoScroller = createAutoScroller(
          { getScrollContainer },
          extendDragToCell
        );
      }

      context.autoScroller.update(x, y);

      return draggingState;
    },

    onPointerUp: (event) => {
      const { x, y } = event.properties;
      const target = getClickTarget(x, y);

      if (target.type === 'cell') {
        const isStaticClick =
          context.pointerDownCell &&
          isSameCell(context.pointerDownCell, target.position) &&
          !context.hasDragged;

        if (isStaticClick && context.wasAlreadySelected) {
          machine.dispatch({
            type: 'startEditing',
            cell: target.position,
            source: 'mouse',
          });
        } else if (context.hasDragged) {
          // Finalize the selection for non-static-click scenarios
          // (i.e. the user actually dragged to a new cell).
          machine.dispatch({
            type: 'extendToCell',
            cell: target.position,
            source: 'mouse',
          });
        }
      }

      context.reset();

      return idleState;
    },
  };

  const fillingState: MouseState = {
    onPointerDown: () => fillingState,

    onPointerMove: (event) => {
      const { x, y } = event.properties;

      extendFillToCell(x, y);

      if (!context.autoScroller) {
        context.autoScroller = createAutoScroller(
          { getScrollContainer },
          extendFillToCell
        );
      }

      context.autoScroller.update(x, y);

      return fillingState;
    },

    onPointerUp: (event) => {
      const { x, y } = event.properties;

      extendFillToCell(x, y);

      if (context.lastValidFillCell) {
        machine.dispatch({
          type: 'fillApply',
          cell: context.lastValidFillCell,
          source: 'mouse',
        });
      }

      machine.dispatch({ type: 'fillEnd', source: 'mouse' });

      context.reset();

      return idleState;
    },
  };

  return idleState;
}
